import { useDeferredValue, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Send, Music4, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "393917041816";
const MAX_VISIBLE = 60; // don't render 5000 rows at once

export default function Richieste() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query);

  useEffect(() => {
    let stop = false;
    axios
      .get(`${API}/music-list`, { timeout: 20000 })
      .then((r) => {
        if (!stop) {
          setSongs(r.data?.songs || []);
          setError(null);
        }
      })
      .catch((e) => { if (!stop) setError(String(e?.response?.data?.detail || e.message)); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = deferred.trim().toLowerCase();
    if (!q) return songs.slice(0, MAX_VISIBLE);
    const tokens = q.split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < songs.length && out.length < MAX_VISIBLE; i++) {
      const s = songs[i];
      const hay = `${s.code} ${s.artist} ${s.title}`.toLowerCase();
      if (tokens.every((t) => hay.includes(t))) out.push(s);
    }
    return out;
  }, [deferred, songs]);

  const totalMatches = useMemo(() => {
    const q = deferred.trim().toLowerCase();
    if (!q) return songs.length;
    const tokens = q.split(/\s+/).filter(Boolean);
    let n = 0;
    for (const s of songs) {
      const hay = `${s.code} ${s.artist} ${s.title}`.toLowerCase();
      if (tokens.every((t) => hay.includes(t))) n++;
    }
    return n;
  }, [deferred, songs]);

  const requestSong = (s) => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`*${s.code}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="richieste"
      data-testid="richieste-section"
      className="border-t border-[#332f2b] bg-[#0f0d0b]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-3 flex items-center gap-2">
            <Music4 size={12} />
            /03 — Richieste
          </div>
          <h2 className="font-anton uppercase text-4xl sm:text-6xl text-[#F5F2EB] leading-none">
            Richiedi <span className="text-[#FF6B00]">una canzone</span>
          </h2>
          <p className="mt-4 font-mono text-sm text-[#A39A8F] max-w-2xl leading-relaxed">
            Scegli tra oltre <span className="text-[#F5F2EB] font-semibold">{songs.length.toLocaleString("it-IT") || "…"}</span> brani. Tocca il brano che vuoi ascoltare: WhatsApp si aprirà con il codice già pronto — <span className="text-[#FF6B00] font-semibold">invialo così com'è</span>, senza aggiungere altro. Ci penseremo noi!
          </p>
        </div>

        {/* Search input */}
        <div className="relative mb-5">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A39A8F]" />
          <input
            data-testid="richieste-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per artista, titolo o codice…"
            className="w-full bg-[#2a2521] border-2 border-[#4a3f33] rounded-lg pl-11 pr-4 py-3.5 text-base text-[#F5F2EB] font-mono placeholder:text-[#A39A8F] focus:outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/30 transition"
          />
        </div>

        {/* Status / count */}
        <div className="mb-4 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest">
          <span className="text-[#A39A8F]" data-testid="richieste-count">
            {loading
              ? "Caricamento…"
              : `${totalMatches.toLocaleString("it-IT")} ${totalMatches === 1 ? "brano" : "brani"} ${query ? "trovati" : "in libreria"}`}
          </span>
          {totalMatches > MAX_VISIBLE && (
            <span className="text-[#69635c]">Mostrati primi {MAX_VISIBLE}</span>
          )}
        </div>

        {loading && (
          <div className="rounded-xl border border-[#332f2b] bg-[#1a1816] p-10 text-center">
            <Loader2 size={32} className="mx-auto animate-spin text-[#FF6B00]" />
            <div className="mt-3 font-mono text-sm text-[#A39A8F]">Sto scaricando la libreria brani…</div>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-[#cf2020]/40 bg-[#cf2020]/10 p-5 font-mono text-sm text-[#ff8a8a] flex items-center gap-3">
            <AlertTriangle size={18} />
            Lista brani non disponibile ({error})
          </div>
        )}

        {!loading && !error && (
          <ul data-testid="richieste-list" className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filtered.map((s) => (
              <li key={s.code}>
                <button
                  type="button"
                  data-testid={`richieste-row-${s.code}`}
                  onClick={() => requestSong(s)}
                  className="group w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-[#4a3f33] bg-[#2a2521] hover:border-[#FF6B00] hover:bg-[#2f2620] active:scale-[0.99] transition text-left min-w-0"
                >
                  <span className="shrink-0 font-anton text-[#FF6B00] text-base sm:text-lg w-14 sm:w-16 text-center tabular-nums">
                    {s.code}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-oswald uppercase text-sm sm:text-base text-[#F5F2EB] leading-tight truncate">
                      {s.title}
                    </span>
                    <span className="block font-mono text-[11px] text-[#A39A8F] truncate mt-0.5">
                      {s.artist}
                    </span>
                  </span>
                  <Send size={16} className="shrink-0 text-[#69635c] group-hover:text-[#FF6B00] transition" />
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="p-8 text-center font-mono text-sm text-[#69635c] border border-dashed border-[#332f2b] rounded-xl">
                Nessun brano trovato con "{query}"
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}
